import React, { useState, useEffect } from 'react';
import { TrendingUp, Plus, Trash2, ClipboardList } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, AIOptionButton, PollResults,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DEFAULT_SETTINGS = { predictionDeadline: '', outcome: '', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, description: false, descText: '' };

const DUMMY_DATA = {
    question: 'Which team will win the next Cricket World Cup?',
    options: [
        { id: 'opt1', label: 'India 🇮🇳' },
        { id: 'opt2', label: 'Australia 🇦🇺' },
        { id: 'opt3', label: 'England 🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
        { id: 'opt4', label: 'Pakistan 🇵🇰' },
        { id: 'opt5', label: 'South Africa 🇿🇦' },
    ],
    settings: { ...DEFAULT_SETTINGS, description: true, descText: 'Cast your prediction before the tournament begins. The actual outcome will be revealed after the final.' },
};

const Prediction = ({ event, activity, isLaunched, onLaunch, onStop, onNext, onPrev, hasNext, hasPrev, onSettingsUpdated }) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [options, setOptions] = useState([{ id: 'opt1', label: 'Option A' }, { id: 'opt2', label: 'Option B' }]);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [isAddingOptions, setIsAddingOptions] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);

    const { content, isLoading, isSaving, saveStatus, saveContent } = useActivityContent(event?.id, activity?.id);

    useEffect(() => {
        if (!content) return;
        setQuestion(content.question || '');
        if (Array.isArray(content.options) && content.options.length) setOptions(content.options);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const buildContent = () => ({ question, options, settings });
    const validate = () => !question.trim() ? 'Please enter a question.' : (options.some(o => !o.label.trim()) ? 'All options need a label.' : null);
    const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));
    const addOption = () => setOptions(prev => [...prev, { id: `opt${Date.now()}`, label: '' }]);
    const removeOption = async (id) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Option',
            message: 'Delete this option?',
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ This poll already has {responseCount} response{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    If any of them predicted this option, it will no longer appear in the results breakdown.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setOptions(prev => prev.filter(o => o.id !== id));
    };
    const updateOption = (id, val) => setOptions(prev => prev.map(o => o.id === id ? { ...o, label: val } : o));

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text: question }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousQuestion(question); setQuestion(data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImproving(false); }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousDescText(settings.descText); updateSetting('descText', data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImprovingDesc(false); }
    };

    const handleUndoQuestion = () => { if (previousQuestion !== null) { setQuestion(previousQuestion); setPreviousQuestion(null); } };
    const handleUndoDesc = () => { if (previousDescText !== null) { updateSetting('descText', previousDescText); setPreviousDescText(null); } };

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setOptions(DUMMY_DATA.options);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const handleAddOptionsWithAI = async () => {
        setIsAddingOptions(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'options', context: { question } }) });
            if (res.ok) { const data = await res.json(); if (data.suggestions) { setOptions(prev => [...prev, ...data.suggestions.map(s => ({ id: `opt${Date.now()}_${Math.random().toString(36).slice(2)}`, label: s }))]); } } else { alert('Failed to generate options. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to generate options. Please try again.'); } finally { setIsAddingOptions(false); }
    };

    const participantPreview = (
        <ParticipantExperience step="activity" name="Preview" event={event} activity={{ ...activity, content: buildContent() }} isPreview />
    );

    const left = (
        <>
            <div className={styles.questionCard}>
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && <AIUndoButton onClick={handleUndoQuestion} label="Undo" />}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input className={styles.questionInput} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Type your prediction question here..." autoFocus onBlur={() => setEditingQuestion(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingQuestion(false); } }} />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingQuestion(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingQuestion(true); } }}>
                                {question || 'Type your prediction question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && <AIUndoButton onClick={handleUndoDesc} label="Undo" />}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input className={styles.descInput} value={settings.descText || ''} onChange={(e) => updateSetting('descText', e.target.value)} placeholder="Type description here..." autoFocus onBlur={() => setEditingDesc(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingDesc(false); } }} />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingDesc(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingDesc(true); } }}>
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}

                    <div className={styles.optionsCard}>
                        <div className={styles.optionsHeader}>
                            <span className={styles.optionsLabel}>Prediction Options</span>
                        </div>
                        {options.map((opt, i) => (
                            <div key={opt.id} className={styles.optionRow}>
                                <span className={styles.optionLetter}>{String.fromCharCode(65 + i)}</span>
                                <input className={styles.optionInput} value={opt.label} onChange={(e) => updateOption(opt.id, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + i)}`} />
                                {options.length > 2 && <button className={styles.optionRemoveBtn} onClick={() => removeOption(opt.id)} aria-label="Remove"><Trash2 size={13} /></button>}
                            </div>
                        ))}
                        <div className={styles.optionsFooter}>
                            <button className={styles.addOptionBtn} onClick={addOption}><Plus size={14} /> Add option</button>
                            <AIOptionButton onClick={handleAddOptionsWithAI} loading={isAddingOptions} />
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                    <label style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: 500 }}>Prediction deadline</label>
                    <input type="datetime-local" value={settings.predictionDeadline || ''} onChange={e => updateSetting('predictionDeadline', e.target.value)} style={{ padding: 'var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                    <label style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: 500 }}>Actual outcome</label>
                    <select value={settings.outcome || ''} onChange={e => updateSetting('outcome', e.target.value)} style={{ padding: 'var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }}>
                        <option value="">Not revealed yet</option>
                        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                </div>
            </div>
        </>
    );

    const right = (
        <SettingsGroup title="Settings">
            <SettingsRow label="Poll description" description="Add description visible to participants">
                <Toggle checked={settings.description} onChange={(e) => updateSetting('description', e.target.checked)} />
            </SettingsRow>
            <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                <Toggle checked={settings.allowUpdateResponse} onChange={(e) => updateSetting('allowUpdateResponse', e.target.checked)} />
            </SettingsRow>
            <PollResults value={settings.pollResults} onChange={v => updateSetting('pollResults', v)} />
        </SettingsGroup>
    );

    return (
        <ActivityLayout
            icon={<TrendingUp size={20} />} color="var(--cat-orange)"
            title="Prediction Poll" eventName={event?.name}
            left={left} right={right} participantPreview={participantPreview}
            onSave={() => saveContent(buildContent())}
            onLoadExample={loadDummy}
            isSaving={isSaving} saveStatus={saveStatus} isLoading={isLoading}
            validate={validate} event={event} activity={activity} isLaunched={isLaunched}
            onLaunch={onLaunch} onStop={onStop} onNext={onNext} onPrev={onPrev}
            hasNext={hasNext} hasPrev={hasPrev} onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default Prediction;
